import { components } from "@appretio/api";
import { removeUndefined } from "@appretio/shared";
import { ContextSelectedOptions } from "./business/contextConfig";
import type { TemporalContext } from "./temporal/temporalConfig";

export function normalizeTemporalOptions(
  temporalOptions: TemporalContext
): TemporalContext {
  const normalizedYears = Array.isArray(temporalOptions.years)
    ? temporalOptions.years
        .map((year) =>
          typeof year === "string" ? Number.parseInt(year, 10) : year
        )
        .filter((year) => !Number.isNaN(year))
    : temporalOptions.years;

  const normalizedAggregationValue: TemporalContext["aggregation_value"] =
    typeof temporalOptions.aggregation_value === "string" &&
    /^\d+$/.test(temporalOptions.aggregation_value)
      ? (() => {
          const parsedValue = Number.parseInt(
            temporalOptions.aggregation_value,
            10
          );

          return parsedValue === 4 ||
            parsedValue === 8 ||
            parsedValue === 12 ||
            parsedValue === 16
            ? parsedValue
            : temporalOptions.aggregation_value;
        })()
      : temporalOptions.aggregation_value;

  return {
    ...temporalOptions,
    years: normalizedYears,
    aggregation_value: normalizedAggregationValue
  };
}

/**
 * The backend splits one flat selection into three contexts. Build the split
 * only through these: a read and a write that disagree on which fields scope a
 * style would let a save delete rows the user never saw.
 */
export function styleContextFrom(
  contextOptions: ContextSelectedOptions
): components["schemas"]["StyleContext"] {
  return {
    brand: contextOptions?.brand,
    sub_brand: contextOptions?.sub_brand,
    product_line: contextOptions?.product_line,
    collection_type: contextOptions?.collection_type,
    product_category: contextOptions?.product_category,
    all_hierarchies: false
  };
}

export function transactionContextFrom(
  contextOptions: ContextSelectedOptions
): components["schemas"]["TransactionContext"] {
  return {
    chain: contextOptions?.chain,
    sales_location: contextOptions?.sales_location,
    country: contextOptions?.country,
    channel: contextOptions?.channel,
    stock_location: contextOptions?.stock_location,
    all_hierarchies: false,
    all_submarkets: false
  };
}

export function styleAssociatedContextFrom(
  contextOptions: ContextSelectedOptions
): components["schemas"]["StyleAssociatedContext"] {
  return { collection_name: contextOptions?.collection_name };
}

/**
 * Converts context and temporal options into the API request format.
 */
export function convertOptionsToApiFormat(
  contextOptions: ContextSelectedOptions,
  temporalOptions: TemporalContext
) {
  return {
    temporal_context: normalizeTemporalOptions(temporalOptions),
    style_context: removeUndefined(styleContextFrom(contextOptions)),
    transaction_context: removeUndefined(
      transactionContextFrom(contextOptions)
    ),
    style_associated_context: removeUndefined(
      styleAssociatedContextFrom(contextOptions)
    )
  };
}
